import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import type { BillingPlan } from '@core/billing-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import type { IconToken } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DividerComponent,
  DropdownComponent,
  HeadingComponent,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';
import { BillingPlansDataService } from '@recruitee/billing-logic';
import { getProductCodenameFromSubscription } from '@recruitee/billing-util';

import { getActiveProductOrThrow } from '../../helpers/get-active-product-or-throw';
import { ready } from '../../helpers/rx-operators';
import type { PricingFeatureLoadedModel } from '../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../types/pricing-feature.types';

enum LimitIndicator {
  Warning = 'warning',
  Danger = 'danger',
}

@Component({
  selector: 'rt-offer-slots-counter',
  styleUrls: ['./offer-slots-counter.component.less'],
  templateUrl: './offer-slots-counter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    HeadingComponent,
    IconComponent,
    OverlayOriginDirective,
    TitleCasePipe,
    TranslatePipe,
    BadgeComponent,
    NgTemplateOutlet,
  ],
})
export class OfferSlotsCounterComponent {
  private readonly model$ = inject(PricingFeatureObservableModel);
  private readonly facade = inject(PricingFeatureFacade);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly INDICATOR_LIMIT: number = 3;
  private readonly billingPlansData = inject(BillingPlansDataService);

  public readonly intercomService = inject(IntercomService);

  @Input() public size: 'small' | 'medium' = 'medium';

  @ViewChild('updatePlanBtnTpl') public updatePlanBtnRef: TemplateRef<void>;
  @ViewChild('updateSlotsBtnTpl') public updateSlotsBtnRef: TemplateRef<void>;
  @ViewChild('intercomBtnTpl') public intercomBtnRef: TemplateRef<void>;

  public model: PricingFeatureLoadedModel;
  public viewModel: {
    slotsUsed: number;
    slotsTotal: number;
    slotsAvailable: number;
    productCode: string | undefined;
    limitIndicator: LimitIndicator | null;
    canManageSubscription: boolean;
    subscriptionManual: boolean;
    isUnlimited?: boolean;
    trial: boolean;
    isPP24: boolean;
  };
  public footerButtonTpl: TemplateRef<void> | null = null;

  public limitIndicatorTokenRecord: Record<LimitIndicator, IconToken> = {
    [LimitIndicator.Warning]: 'colorIconWarningDefault',
    [LimitIndicator.Danger]: 'colorIconCriticalDefault',
  };

  private data$ = combineLatest([
    this.model$.pipe(ready),
    this.billingPlansData.collection$,
    this.billingPlansData.activePlan$,
  ]).pipe(
    map(([model, billingPlans, activePlan]) => {
      const { jobSlotsUsed, slotsTotal, subscription, canManageSubscription } = model;

      const slotsUsed = jobSlotsUsed;
      const slotsAvailable = slotsTotal - slotsUsed;

      const viewModel = {
        slotsUsed,
        slotsTotal,
        slotsAvailable,
        productCode: getProductCodenameFromSubscription(subscription) || model.trial?.productCode,
        limitIndicator: this.resolveLimitIndicator(slotsAvailable),
        canManageSubscription,
        subscriptionManual: subscription?.autoCollection === false,
        isUnlimited: activePlan?.unlimited,
        trial: !!model.trial,
        isPP24: model.isPP24,
      };

      return { viewModel, model, activePlan, billingPlans };
    }),
  );
  private ngOnDestroy$ = new Subject<void>();
  private activePlan: BillingPlan | undefined;
  private billingPlans: BillingPlan[];

  constructor() {
    this.facade.fetchPlansIfNeeded();
  }

  public ngAfterViewInit(): void {
    this.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ viewModel, model, activePlan, billingPlans }) => {
        this.viewModel = viewModel;
        this.model = model;
        this.activePlan = activePlan;
        this.billingPlans = billingPlans;
        this.footerButtonTpl = this.resolveButtonTpl(viewModel);
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public update(): void {
    this.facade.openSlotsPickerDialog(
      getActiveProductOrThrow(this.model, this.billingPlans, this.activePlan),
      this.model,
      this.model.subscription?.manual,
      this.vcRef,
    );
  }

  public upgrade(): void {
    this.facade.openPlanPicker();
  }

  private resolveButtonTpl(viewModel: typeof this.viewModel): TemplateRef<void> | null {
    const { trial, isUnlimited, subscriptionManual, canManageSubscription } = viewModel;
    if (!canManageSubscription) return null;
    else if (isUnlimited || subscriptionManual) return this.intercomBtnRef;
    else if (trial || viewModel.isPP24) return this.updatePlanBtnRef;
    return this.updateSlotsBtnRef;
  }

  private resolveLimitIndicator(slotsAvailable?: number): LimitIndicator | null {
    if ((!slotsAvailable && slotsAvailable !== 0) || slotsAvailable > this.INDICATOR_LIMIT)
      return null;
    return slotsAvailable === 0 ? LimitIndicator.Danger : LimitIndicator.Warning;
  }
}
